import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { buttonSize, buttonVariant } from '@models/button-options.enum';
import { mimicNameValidator } from 'icreate/common/validators/mimic/mimic-validator';
import { RootItem } from 'icreate/common/models/mimic-folder';
import { ButtonLabel, DuplicateItemType } from '@helpers/constants';
import { Utils } from '@helpers/utils';
import { InputWithErrorComponent } from '@components/input-with-error/input-with-error.component';

enum SelectionType {
    ALL = 'all',
    SELECTED = 'selected',
}
interface DuplicateDialogData {
    title: string;
    description?: string;
    initialName: string;
    existingNames: string[];
    items?: RootItem[];
    type: DuplicateItemType;
    selectionLabel?: string;
    autoIncrementName: boolean;
    submitButtonLabel: ButtonLabel;
    showExtraOptions?: boolean;
}
@Component({
    selector: 'app-duplicate-mimic-dialog',
    templateUrl: './duplicate-mimic-dialog.component.html',
    styleUrls: ['./duplicate-mimic-dialog.component.css'],
    standalone: false,
})
export class DuplicateMimicDialogComponent {
    readonly BUTTON_MEDIUM_SIZE = buttonSize.medium;
    readonly BUTTON_TEXT = buttonVariant.text;
    readonly ALL = SelectionType.ALL;
    readonly SELECTED = SelectionType.SELECTED;

    isMoveDialog: boolean = false;
    selectionType = SelectionType.ALL;
    name: string = '';
    titleError: string;
    selectionLabel? = this.data.selectionLabel || '';

    validatorFunction = mimicNameValidator();
    selectedItems: RootItem[] = [];
    @ViewChild(InputWithErrorComponent) nameInput: InputWithErrorComponent;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: DuplicateDialogData,
        private dialogRef: MatDialogRef<DuplicateMimicDialogComponent>,
        private translateService: TranslateService
    ) {}

    get translatedTitle(): string {
        return this.isMoveDialog
            ? this.translateService.instant(this.data.title)
            : `${this.translateService.instant(this.data.title)} "${this.data.initialName}".`;
    }

    ngOnInit() {
        const takenNames = new Set<string>(this.data.existingNames);
        const mimicName = this.data.autoIncrementName
            ? Utils.nextDuplicateName(this.data.initialName, takenNames)
            : this.data.initialName;
        this.name = mimicName;
        if (this.data.showExtraOptions) {
            this.selectedItems = [...(this.data.items || [])];
        }
        if (this.data.submitButtonLabel === ButtonLabel.MOVE) {
            this.isMoveDialog = true;
        }
        this.titleIsRepeated();
    }

    onItemsChange(event: { name: string; value: RootItem[] }) {
        this.selectedItems = event.value;
    }

    onSelectionTypeChange(selectionType: SelectionType) {
        this.selectionType = selectionType;

        if (selectionType === SelectionType.ALL) {
            this.selectedItems = [...(this.data.items || [])];
        } else {
            this.selectedItems = [];
        }
    }
    ngAfterViewInit() {
        queueMicrotask(() => {
            this.nameInput.checkInputErrors();
        });
    }

    get hasInputErrors(): boolean {
        return Object.keys(this.nameInput?.validationErrors || {}).length > 0;
    }

    titleIsRepeated(): void {
        let hasError = false;

        if (this.data?.existingNames?.length == 0) {
            this.titleError = undefined;
            return;
        }

        hasError = this.data.existingNames?.some(el => {
            if (this.data.type != DuplicateItemType.LAYER) return el == this.name;
            return el == this.name && this.name != this.data.initialName;
        });
        if (!hasError) {
            this.titleError = undefined;
        } else {
            this.titleError = this.translateService.instant('mimic.duplicate.error.title.repeat', {
                type: this.data.type,
            });
        }
        return;
    }

    getPluralLabel(key: string): string {
        if (!key || key.trim() === '') return '';
        return `${key}.plural`;
    }

    nameChange(value: string) {
        this.name = value;
        this.titleIsRepeated();
    }

    saveButtonClick() {
        if (this.titleError || Object.keys(this.nameInput?.validationErrors || {}).length > 0) {
            return;
        }
        this.dialogRef.close({
            name: this.name,
            selectedOptions: this.selectedItems,
        });
    }

    cancelButtonClick() {
        this.dialogRef.close();
    }

    get disableLayerEdit() {
        if (this.data.type != DuplicateItemType.LAYER) return false;
        return this.name == this.data.initialName;
    }
}
